import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { Search, Calendar, ArrowRight, TrendingUp, Tag as TagIcon, FolderOpen, LayoutGrid, List, Rows3 } from "lucide-react";
import { PageShell, PageHero } from "@/components/site/page-shell";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const PAGE_SIZE = 8;

type ViewMode = "grid" | "list" | "compact";
type BlogSearch = { q: string; cat: string; page: number; view: ViewMode };

export const Route = createFileRoute("/blog")({
  validateSearch: (search: Record<string, unknown>): BlogSearch => ({
    q: typeof search.q === "string" ? search.q : "",
    cat: typeof search.cat === "string" ? search.cat : "",
    page: Math.max(1, Number(search.page) || 1),
    view: search.view === "list" || search.view === "compact" ? search.view : "grid",
  }),
  component: BlogPage,
  head: () => ({
    meta: [
      { title: "Blog — CoderLity" },
      { name: "description", content: "Engineering, design, AI and growth insights from the CoderLity team." },
      { property: "og:title", content: "Blog — CoderLity" },
      { property: "og:description", content: "Engineering, design, AI and growth insights from the CoderLity team." },
    ],
    links: [{ rel: "canonical", href: "https://coderlity.com/blog" }],
  }),
});

type Post = {
  id: string; title: string; slug: string; excerpt: string | null;
  cover_url: string | null; category_id: string | null;
  published_at: string | null; created_at: string;
};

type Category = { id: string; name: string; slug: string };

function BlogPage() {
  const { q, cat, page, view } = Route.useSearch();
  const navigate = useNavigate({ from: "/blog" });

  const { data: posts, isLoading } = useQuery({
    queryKey: ["public", "posts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("posts")
        .select("id,title,slug,excerpt,cover_url,category_id,published_at,created_at")
        .eq("published", true)
        .order("published_at", { ascending: false, nullsFirst: false })
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as Post[];
    },
  });

  const { data: categories } = useQuery({
    queryKey: ["public", "categories"],
    queryFn: async () => {
      const { data, error } = await supabase.from("categories").select("id,name,slug").order("sort_order");
      if (error) throw error;
      return data as Category[];
    },
  });

  const catMap = useMemo(() => new Map((categories ?? []).map((c) => [c.id, c])), [categories]);
  const catBySlug = useMemo(() => new Map((categories ?? []).map((c) => [c.slug, c])), [categories]);

  const filtered = useMemo(() => {
    let list = posts ?? [];
    if (cat) {
      const c = catBySlug.get(cat);
      if (c) list = list.filter((p) => p.category_id === c.id);
    }
    if (q.trim()) {
      const term = q.trim().toLowerCase();
      list = list.filter(
        (p) =>
          p.title.toLowerCase().includes(term) ||
          (p.excerpt ?? "").toLowerCase().includes(term),
      );
    }
    return list;
  }, [posts, cat, q, catBySlug]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const recent = (posts ?? []).slice(0, 5);

  // Tag cloud: derive from category counts
  const tagCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of posts ?? []) {
      if (!p.category_id) continue;
      counts.set(p.category_id, (counts.get(p.category_id) ?? 0) + 1);
    }
    return counts;
  }, [posts]);

  const setSearch = (term: string) =>
    navigate({ search: (prev: BlogSearch) => ({ ...prev, q: term, page: 1 }) });
  const setCat = (slug: string) =>
    navigate({ search: (prev: BlogSearch) => ({ ...prev, cat: slug, page: 1 }) });
  const setPage = (n: number) =>
    navigate({ search: (prev: BlogSearch) => ({ ...prev, page: n }) });
  const setView = (v: ViewMode) =>
    navigate({ search: (prev: BlogSearch) => ({ ...prev, view: v }) });

  return (
    <PageShell>
      <PageHero
        eyebrow="Blog"
        title="Insights from the build floor"
        sub="Tactical writeups on shipping software, scaling SaaS and growing in MENA and beyond."
      />

      <section className="py-12 lg:py-16">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-[1fr_260px]">
            {/* Main column */}
            <div>
              {/* Search + active filter */}
              <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="relative w-full sm:max-w-md">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={q}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search articles…"
                    className="h-11 pl-10"
                    maxLength={200}
                  />
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-sm text-muted-foreground">
                    {filtered.length} {filtered.length === 1 ? "article" : "articles"}
                    {cat && catBySlug.get(cat) && (
                      <>
                        {" "}in{" "}
                        <button onClick={() => setCat("")} className="font-semibold text-primary hover:underline">
                          {catBySlug.get(cat)!.name} ✕
                        </button>
                      </>
                    )}
                  </div>
                  <div className="inline-flex rounded-lg border border-border bg-card p-0.5">
                    <ViewBtn active={view === "grid"} onClick={() => setView("grid")} label="Grid view"><LayoutGrid className="h-4 w-4" /></ViewBtn>
                    <ViewBtn active={view === "compact"} onClick={() => setView("compact")} label="Compact view"><Rows3 className="h-4 w-4" /></ViewBtn>
                    <ViewBtn active={view === "list"} onClick={() => setView("list")} label="List view"><List className="h-4 w-4" /></ViewBtn>
                  </div>
                </div>
              </div>

              {isLoading && <p className="text-center text-muted-foreground py-16">Loading articles…</p>}

              {!isLoading && !paginated.length && (
                <div className="rounded-2xl border border-dashed border-border p-12 text-center">
                  <p className="text-muted-foreground">No articles match your filters.</p>
                  {(q || cat) && (
                    <Button variant="ghost" size="sm" className="mt-3" onClick={() => navigate({ search: () => ({ q: "", cat: "", page: 1, view }) })}>
                      Clear filters
                    </Button>
                  )}
                </div>
              )}

              {/* Items */}
              {view === "list" ? (
                <div className="flex flex-col gap-4">
                  {paginated.map((p) => {
                    const c = p.category_id ? catMap.get(p.category_id) : null;
                    return <PostRow key={p.id} post={p} category={c ?? null} />;
                  })}
                </div>
              ) : view === "compact" ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  {paginated.map((p) => {
                    const c = p.category_id ? catMap.get(p.category_id) : null;
                    return <PostRow key={p.id} post={p} category={c ?? null} />;
                  })}
                </div>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                  {paginated.map((p, i) => {
                    const c = p.category_id ? catMap.get(p.category_id) : null;
                    return <PostCard key={p.id} post={p} category={c ?? null} index={i} />;
                  })}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-12 flex items-center justify-center gap-2">
                  <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)}>
                    Previous
                  </Button>
                  {Array.from({ length: totalPages }).map((_, i) => {
                    const n = i + 1;
                    return (
                      <Button
                        key={n}
                        variant={n === currentPage ? "default" : "outline"}
                        size="sm"
                        className="h-9 w-9 p-0"
                        onClick={() => setPage(n)}
                      >
                        {n}
                      </Button>
                    );
                  })}
                  <Button variant="outline" size="sm" disabled={currentPage === totalPages} onClick={() => setPage(currentPage + 1)}>
                    Next
                  </Button>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <aside className="space-y-8 lg:sticky lg:top-24 lg:self-start">
              {/* Categories */}
              <div className="rounded-2xl border border-border bg-card p-6">
                <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-foreground">
                  <FolderOpen className="h-4 w-4 text-primary" /> Categories
                </h3>
                <ul className="space-y-1.5">
                  <li>
                    <button
                      onClick={() => setCat("")}
                      className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm transition ${
                        !cat ? "bg-primary/10 text-primary font-semibold" : "hover:bg-secondary text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      <span>All posts</span>
                      <span className="text-xs">{posts?.length ?? 0}</span>
                    </button>
                  </li>
                  {(categories ?? []).map((c) => {
                    const count = tagCounts.get(c.id) ?? 0;
                    if (count === 0) return null;
                    return (
                      <li key={c.id}>
                        <button
                          onClick={() => setCat(c.slug)}
                          className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm transition ${
                            cat === c.slug ? "bg-primary/10 text-primary font-semibold" : "hover:bg-secondary text-muted-foreground hover:text-foreground"
                          }`}
                        >
                          <span>{c.name}</span>
                          <span className="text-xs">{count}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Recent */}
              <div className="rounded-2xl border border-border bg-card p-6">
                <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-foreground">
                  <TrendingUp className="h-4 w-4 text-primary" /> Recent Posts
                </h3>
                <ul className="space-y-4">
                  {recent.map((p) => (
                    <li key={p.id}>
                      <Link to="/blog/$slug" params={{ slug: p.slug }} search={{ q: "", cat: "", page: 1, view: "grid" }} className="group flex gap-3">
                        {p.cover_url ? (
                          <img src={p.cover_url} alt="" className="h-14 w-14 flex-shrink-0 rounded-lg object-cover" loading="lazy" />
                        ) : (
                          <div className="h-14 w-14 flex-shrink-0 rounded-lg bg-gradient-to-br from-primary/30 to-primary/10" />
                        )}
                        <div className="min-w-0">
                          <p className="line-clamp-2 text-sm font-medium leading-snug group-hover:text-primary transition">
                            {p.title}
                          </p>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {format(new Date(p.published_at ?? p.created_at), "MMM d, yyyy")}
                          </p>
                        </div>
                      </Link>
                    </li>
                  ))}
                  {!recent.length && <li className="text-sm text-muted-foreground">No posts yet.</li>}
                </ul>
              </div>

              {/* Tags */}
              <div className="rounded-2xl border border-border bg-card p-6">
                <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-foreground">
                  <TagIcon className="h-4 w-4 text-primary" /> Tags
                </h3>
                <div className="flex flex-wrap gap-2">
                  {(categories ?? []).map((c) => {
                    const count = tagCounts.get(c.id) ?? 0;
                    if (count === 0) return null;
                    const active = cat === c.slug;
                    return (
                      <button
                        key={c.id}
                        onClick={() => setCat(active ? "" : c.slug)}
                        className={`rounded-full border px-3 py-1 text-xs transition ${
                          active
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border text-muted-foreground hover:border-primary/40 hover:text-foreground"
                        }`}
                      >
                        #{c.name}
                      </button>
                    );
                  })}
                  {!(categories ?? []).some((c) => (tagCounts.get(c.id) ?? 0) > 0) && (
                    <p className="text-sm text-muted-foreground">No tags yet.</p>
                  )}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </PageShell>
  );
}

function FeaturedCard({ post, category }: { post: Post; category: Category | null }) {
  const date = post.published_at ?? post.created_at;
  return (
    <Link
      to="/blog/$slug"
      params={{ slug: post.slug }}
      search={{ q: "", cat: "", page: 1, view: "grid" }}
      className="group mb-8 grid overflow-hidden rounded-3xl border border-border bg-card transition hover:border-primary/40 md:grid-cols-2"
    >
      {post.cover_url ? (
        <img src={post.cover_url} alt={post.title} className="aspect-[16/10] w-full object-cover md:aspect-auto md:h-full" loading="lazy" />
      ) : (
        <div className="aspect-[16/10] bg-gradient-to-br from-violet-500 via-fuchsia-500 to-amber-400 md:aspect-auto md:h-full" />
      )}
      <div className="flex flex-col justify-center p-6 md:p-10">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Badge variant="default">Featured</Badge>
          {category && (
            <span className="rounded-full bg-secondary px-2 py-0.5 font-semibold text-foreground">{category.name}</span>
          )}
          <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {format(new Date(date), "MMM d, yyyy")}</span>
        </div>
        <h2 className="mt-4 text-2xl font-bold leading-tight group-hover:text-primary transition md:text-3xl">
          {post.title}
        </h2>
        {post.excerpt && <p className="mt-3 text-muted-foreground line-clamp-3">{post.excerpt}</p>}
        <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-primary">
          Read article <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
}

function PostCard({ post, category, index }: { post: Post; category: Category | null; index: number }) {
  const date = post.published_at ?? post.created_at;
  const gradients = ["from-violet-500 to-fuchsia-500", "from-sky-500 to-cyan-500", "from-amber-500 to-pink-500", "from-emerald-500 to-teal-500"];
  return (
    <Link
      to="/blog/$slug"
      params={{ slug: post.slug }}
      search={{ q: "", cat: "", page: 1, view: "grid" }}
      className="group flex flex-col overflow-hidden rounded-xl border border-border bg-card transition hover:-translate-y-1 hover:border-primary/40 hover:shadow-md"
    >
      {post.cover_url ? (
        <img src={post.cover_url} alt={post.title} className="aspect-[16/9] w-full object-cover" loading="lazy" />
      ) : (
        <div className={`aspect-[16/9] bg-gradient-to-br ${gradients[index % gradients.length]}`} />
      )}
      <div className="flex flex-1 flex-col p-3">
        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
          {category && (
            <span className="rounded-full bg-secondary px-1.5 py-0.5 font-semibold text-foreground">{category.name}</span>
          )}
          <span className="flex items-center gap-1"><Calendar className="h-2.5 w-2.5" /> {format(new Date(date), "MMM d, yyyy")}</span>
        </div>
        <h3 className="mt-2 line-clamp-2 text-sm font-semibold leading-snug group-hover:text-primary transition">{post.title}</h3>
        {post.excerpt && <p className="mt-1.5 line-clamp-2 text-xs text-muted-foreground">{post.excerpt}</p>}
        <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary">
          Read more <ArrowRight className="h-3 w-3 transition group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
}

function ViewBtn({ active, onClick, label, children }: { active: boolean; onClick: () => void; label: string; children: ReactNode }) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      title={label}
      className={`rounded-md p-1.5 transition ${active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}
    >
      {children}
    </button>
  );
}

function PostRow({ post, category }: { post: Post; category: Category | null }) {
  const date = post.published_at ?? post.created_at;
  const words = (post.excerpt ?? "").split(/\s+/).filter(Boolean).length;
  const readMins = Math.max(1, Math.round(words / 200));
  return (
    <Link
      to="/blog/$slug"
      params={{ slug: post.slug }}
      search={{ q: "", cat: "", page: 1, view: "grid" }}
      className="group flex w-full gap-5 overflow-hidden rounded-xl border border-border bg-card p-4 transition hover:border-primary/40 hover:shadow-md"
    >
      {post.cover_url ? (
        <img src={post.cover_url} alt={post.title} className="h-28 w-40 flex-shrink-0 rounded-lg object-cover sm:h-32 sm:w-52" loading="lazy" />
      ) : (
        <div className="h-28 w-40 flex-shrink-0 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 sm:h-32 sm:w-52" />
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
          {category && (
            <span className="rounded-full bg-secondary px-2 py-0.5 font-semibold text-foreground">{category.name}</span>
          )}
          <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {format(new Date(date), "MMM d, yyyy")}</span>
          <span className="text-muted-foreground/70">·</span>
          <span>{readMins} min read</span>
        </div>
        <h3 className="mt-2 line-clamp-2 text-lg font-semibold leading-snug group-hover:text-primary transition">{post.title}</h3>
        {post.excerpt && <p className="mt-1.5 line-clamp-2 text-sm text-muted-foreground">{post.excerpt}</p>}
        <div className="mt-auto flex items-center justify-between gap-3 pt-3">
          {category && (
            <span className="text-xs text-muted-foreground">
              <TagIcon className="mr-1 inline h-3 w-3" />#{category.slug}
            </span>
          )}
          <span className="ml-auto inline-flex items-center gap-1 text-sm font-semibold text-primary">
            Read article <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
          </span>
        </div>
      </div>
    </Link>
  );
}
